// Turn the solver's square grid into smooth, rounded height bands for display.
//
// Marching squares (d3-contour) traces each band boundary with sub-cell interpolation, Chaikin
// corner-cutting rounds it off, and the bands are painted opaque onto a canvas at several times
// the grid resolution. The map shows that canvas semi-transparently, plus the outer boundary as
// a crisp vector line. The solver's cells are still used for tapping and the route.

import { contours } from 'd3-contour';
import type { GridSpec, LngLat } from './geo';

export type Ring = [number, number][];
/** One band: everything at or above `threshold`, as polygons (outer ring + holes). */
export interface Band {
  threshold: number;
  polygons: Ring[][];
}

/**
 * Smoothed iso-polygons for each threshold. `values` is row-major with NaN for "no value";
 * NaN cells are given `outside`, which should sit a little below the first threshold so the
 * outer boundary falls part-way between the last reached cell and the first unreached one.
 * Coordinates are in grid units: x = 0 is the west edge of the first column.
 */
export function smoothBands(values: ArrayLike<number>, nx: number, ny: number, thresholds: number[], outside: number, iterations = 2): Band[] {
  const raw = new Float64Array(values.length);
  for (let i = 0; i < raw.length; i++) raw[i] = Number.isNaN(values[i]) ? outside : values[i];
  // A light 3x3 binomial blur rounds off one-cell stair steps before tracing. It may only move
  // the boundary between cells, never across one: reached cells stay above the first threshold
  // and unreached cells below it, so nothing reachable is hidden and nothing else is shown.
  const v = blur3(raw, nx, ny);
  const edge = Math.abs(outside) * 0.3;
  const t0 = thresholds[0] ?? 0;
  for (let i = 0; i < v.length; i++) {
    if (Number.isNaN(values[i])) v[i] = Math.min(v[i], t0 - edge);
    else if (values[i] >= t0) v[i] = Math.max(v[i], t0 + edge);
  }
  const gen = contours().size([nx, ny]).smooth(true);
  return thresholds.map((t) => {
    const mp = gen.contour(Array.from(v), t);
    return {
      threshold: t,
      polygons: mp.coordinates.map((poly) => poly.map((ring) => chaikin(relax(ring as Ring, 0.5, 2), iterations))),
    };
  });
}

function blur3(src: Float64Array, nx: number, ny: number): Float64Array {
  const tmp = new Float64Array(src.length);
  const out = new Float64Array(src.length);
  for (let r = 0; r < ny; r++)
    for (let c = 0; c < nx; c++) {
      const i = r * nx + c;
      tmp[i] = (src[i - (c > 0 ? 1 : 0)] + 2 * src[i] + src[i + (c < nx - 1 ? 1 : 0)]) / 4;
    }
  for (let r = 0; r < ny; r++)
    for (let c = 0; c < nx; c++) {
      const i = r * nx + c;
      out[i] = (tmp[i - (r > 0 ? nx : 0)] + 2 * tmp[i] + tmp[i + (r < ny - 1 ? nx : 0)]) / 4;
    }
  return out;
}

/**
 * Resample a closed ring every `spacing` grid units and replace each point by the average of its
 * neighbours within `half` samples either side. Removes cell-sized notches; the edge moves by
 * well under one cell.
 */
export function relax(ring: Ring, spacing: number, half: number): Ring {
  const pts = ring.slice(0, -1);
  if (pts.length < 4) return ring;
  // resample by arc length
  const out: Ring = [];
  let carry = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    let d = carry;
    while (d < len) {
      const t = d / len;
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
      d += spacing;
    }
    carry = d - len;
  }
  if (out.length < 2 * half + 3) return ring;
  const n = out.length;
  const sm: Ring = out.map((_, i) => {
    let x = 0;
    let y = 0;
    for (let k = -half; k <= half; k++) {
      const p = out[(i + k + n) % n];
      x += p[0];
      y += p[1];
    }
    return [x / (2 * half + 1), y / (2 * half + 1)];
  });
  sm.push(sm[0]);
  return sm;
}

/** Chaikin corner cutting on a closed ring (first point repeated at the end, as GeoJSON). */
export function chaikin(ring: Ring, iterations: number): Ring {
  let pts = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? ring.slice(0, -1) : ring.slice();
  if (pts.length < 3) return ring;
  for (let k = 0; k < iterations; k++) {
    const out: Ring = [];
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      out.push([0.75 * a[0] + 0.25 * b[0], 0.75 * a[1] + 0.25 * b[1]]);
      out.push([0.25 * a[0] + 0.75 * b[0], 0.25 * a[1] + 0.75 * b[1]]);
    }
    pts = out;
  }
  pts.push(pts[0]);
  return pts;
}

/** Paint bands (ascending thresholds) opaquely onto a canvas `scale` times the grid size. */
export function paintBands(bands: Band[], colors: string[], nx: number, ny: number, maxSide = 2048): HTMLCanvasElement {
  const scale = Math.max(1, Math.min(8, Math.floor(maxSide / Math.max(nx, ny))));
  const canvas = document.createElement('canvas');
  canvas.width = nx * scale;
  canvas.height = ny * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  bands.forEach((b, i) => {
    ctx.fillStyle = colors[i];
    ctx.beginPath();
    for (const poly of b.polygons)
      for (const ring of poly) {
        ring.forEach(([x, y], j) => (j ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
        ctx.closePath();
      }
    ctx.fill('evenodd');
  });
  return canvas;
}

/** Grid units -> lon/lat (x = 0 west edge, y = 0 north edge). */
export function gridToLngLat(spec: GridSpec, x: number, y: number): LngLat {
  return [spec.lng0 + (x - 0.5 - spec.cx) * spec.dLng, spec.lat0 - (y - 0.5 - spec.cy) * spec.dLat];
}

/** The outer boundary of a band as GeoJSON lines, for a crisp outline at any zoom. */
export function bandOutline(spec: GridSpec, band: Band): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: band.polygons.flatMap((poly) =>
      poly.map((ring) => ({
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'LineString' as const, coordinates: ring.map(([x, y]) => gridToLngLat(spec, x, y)) },
      })),
    ),
  };
}
